import React, { useState, useRef, useEffect } from "react";
import { Timestamp } from "firebase/firestore";

/* UI */
import { motion, useAnimation } from "motion/react";
import {
  Card,
  Input,
  Textarea,
  Button,
  Separator,
  Collapsible,
  CollapsibleContent,
} from "@lemonadetraining/web-ui";
import { IconCirclePlus } from "@tabler/icons-react";

/* Own Modules */
import TimelineWeekDay from "./TimelineWeekDay";
import { WorkoutData, Workout, Movement } from "@lemonadetraining/contracts";
import { ProgrammingType } from "../../Common/product.ts";
import AddYouTube from "../WorkoutCard/AddYouTube";
import DatePickerWithChip from "../DatePickerWithChip";
import WorkoutTags from "./WorkoutTags";
import { MultipleYouTubeView } from "../WorkoutCard/MultipleYouTubeView";
import MovementItem from "./MovementItem.tsx";
import WorkoutScore from "./WorkoutScore.js";
import MyModal from "@lemonadetraining/web-shared/components/MyModal";
import WorkoutAdditionalAction from "./WorkoutAdditionalAction.js";

const additionalActionVariant = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0.0, y: -5 },
};

/*
 * ======================================================
 * Workout Card Component
 * ======================================================
 */
interface Props {
  UID?: string;
  programmingUID: string;
  CoachUID: string;
  initialWorkoutData?: WorkoutData;
  editable?: boolean;
  programmingType: ProgrammingType;
  /** The gym's zone for recurring dates; null = this browser's calendar. */
  gymZone: string | null;
  hideActions?: boolean;
  isMyWorkout?: boolean;
  /** Read-only card rendered as text, not as a disabled form. */
  readOnlyView?: boolean;
  onCopy?: (workout: Workout, forceCopyToToday: boolean) => void;
  onSaveEdit?: (workout: Workout) => void;
  onRemove?: (workout: Workout) => void;
  onCardUpdate?: (workoutData: WorkoutData) => void;
}

const WorkoutCard: React.FC<Props> = ({
  UID,
  programmingUID,
  CoachUID,
  initialWorkoutData,
  editable,
  programmingType,
  gymZone,
  hideActions = false,
  isMyWorkout = false,
  readOnlyView = false,
  onCopy,
  onSaveEdit,
  onRemove,
  onCardUpdate,
}) => {
  const actionSectionAnimate = useAnimation();
  const isInitRef = useRef(false);
  /* Use States */
  const [workoutData, setWorkoutData] = useState<WorkoutData>(
    initialWorkoutData || {
      title: "",
      description: "",
      date: "",
      tags: [],
      youTubeIDs: [],
      movements: [],
    }
  );
  const [editMode, setEditMode] = useState(editable || false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const movIndexRef = useRef(workoutData.movements?.length || 0);
  const workoutUIDRef = useRef(UID || null);

  /* ---- [passing data to parents] -----------  */
  useEffect(() => {
    if (!isInitRef.current) {
      isInitRef.current = true;
      return;
    }

    if (onCardUpdate) {
      onCardUpdate(workoutData);
    }
  }, [workoutData]); // eslint-disable-line react-hooks/exhaustive-deps -- reports the user's edits only, not a new callback

  /* Animation ------------ */
  const onMouserIsOverCard = async () => {
    actionSectionAnimate.start("visible");
  };

  const onMouserIsOutCard = async () => {
    actionSectionAnimate.start("hidden");
  };

  /* Handlers ------------ */
  const onYouTubeIDsChanged = (youTubeIDs: string[]) => {
    setWorkoutData((prevData) => ({ ...prevData, youTubeIDs }));
  };

  const onAddMovementHandler = () => {
    movIndexRef.current++;
    const newMovement: Movement = { id: movIndexRef.current };
    setWorkoutData((prevData) => ({
      ...prevData,
      movements: [...prevData.movements, newMovement],
    }));
  };

  const onRemoveMovementHandler = (id: number) => {
    setWorkoutData((prevData) => ({
      ...prevData,
      movements: prevData.movements?.filter((mov) => mov.id !== id),
    }));
  };

  /* Actions Handlers ------------ */
  const keepPreviousWorkoutData = useRef<WorkoutData>(workoutData);

  const onEditHandle = () => {
    setEditMode(true);
  };

  const onCancelEditHandle = () => {
    setWorkoutData(keepPreviousWorkoutData.current);
    setEditMode(false);
  };

  const onCopyHandle = () => {
    if (onCopy) {
      const theWorkout: Workout = {
        UID: undefined,
        CoachUID: CoachUID,
        programmingUID: programmingUID,
        workoutData: {
          ...workoutData,
          date: Timestamp.now(),
        },
        hasRecordForThisWorkout: false,
      };
      onCopy(theWorkout, true);
    }
  };

  const onSaveHandle = () => {
    if (onSaveEdit) {
      const theWorkout: Workout = {
        UID: workoutUIDRef.current || "",
        CoachUID: CoachUID,
        programmingUID: programmingUID,
        workoutData: workoutData,
      };
      onSaveEdit(theWorkout);
    }

    keepPreviousWorkoutData.current = workoutData;
    setEditMode(false);
  };

  const getMovementDataCallBack = (movement: Movement) => {
    const index: any = workoutData.movements?.findIndex((mov) => mov.id === movement.id);
    if (index === -1) return;
    const newMovements = [...workoutData.movements];
    newMovements[index] = movement;
    setWorkoutData({ ...workoutData, movements: newMovements });
  };

  const onDeleteWorkoutHandle = () => {
    if (onRemove) {
      const theWorkout: Workout = {
        UID: workoutUIDRef.current || "",
        CoachUID: CoachUID,
        programmingUID: programmingUID,
        workoutData: workoutData,
      };
      onRemove(theWorkout);
    }
  };

  /* ====================[UI Return]===================== */
  return (
    <motion.div onMouseOver={onMouserIsOverCard} onMouseOut={onMouserIsOutCard}>
      <Card className="max-w-[600px] p-4">
        <div className="flex flex-row gap-1">
          {readOnlyView ? (
            <h3 className="min-w-0 flex-1 self-center break-words text-base font-semibold">
              {workoutData.title}
            </h3>
          ) : (
            <Input
              placeholder="Title"
              className="flex-1"
              value={workoutData.title}
              onChange={(e) => {
                setWorkoutData({ ...workoutData, title: e.target.value });
              }}
              disabled={!editMode}
            />
          )}
          <div className="flex flex-row items-center justify-end">
            <AddYouTube
              youTubeIDs={workoutData.youTubeIDs || []}
              onAddYouTubeLink={onYouTubeIDsChanged}
              readOnly={!editMode}
            />
            {programmingType === "reoccurring" && (
              <DatePickerWithChip
                value={workoutData.date}
                onGetDateObj={(date: any) => setWorkoutData({ ...workoutData, date })}
                editMode={editMode}
                zone={gymZone}
              />
            )}
            {programmingType === "timeline" && (
              <TimelineWeekDay
                week={workoutData?.week}
                day={workoutData?.day}
                onWeekUpdate={(week: number) => {
                  setWorkoutData({
                    ...workoutData,
                    week,
                    day: workoutData.day || 0,
                  });
                }}
                onDayUpdate={(day: number) => {
                  setWorkoutData({
                    ...workoutData,
                    week: workoutData.week || 0,
                    day,
                  });
                }}
                editMode={editMode}
              />
            )}
            <WorkoutTags
              value={workoutData?.tags || []}
              onTagUpdate={(tags: string[]) => {
                setWorkoutData({ ...workoutData, tags });
              }}
              readOnly={!editMode}
            />
          </div>
        </div>
        <Separator className="my-2" />
        <div className="flex flex-col items-center justify-start gap-1">
          {workoutData.youTubeIDs?.length > 0 && (
            <div className="mb-1">
              <MultipleYouTubeView youTubeIDs={workoutData.youTubeIDs} />
            </div>
          )}
          {/* No `rows`: a workout description is a list, not a caption, and it
              is the same box athletes read the workout from. It grows with the
              content (Textarea handles that) from a three-line floor. */}
          {readOnlyView ? (
            workoutData.description && (
              <p className="mb-2 w-full whitespace-pre-line break-words text-sm">
                {workoutData.description}
              </p>
            )
          ) : (
            <Textarea
              placeholder="Workout Description"
              className="mb-2 min-h-24 w-full"
              value={workoutData.description}
              onChange={(e) => {
                setWorkoutData({ ...workoutData, description: e.target.value });
              }}
              disabled={!editMode}
            />
          )}
        </div>
        <div className="flex flex-col justify-start">
          {workoutData.movements?.map((movement) => (
            <Collapsible key={movement.id} defaultOpen>
              <CollapsibleContent>
                <MovementItem
                  id={movement.id}
                  movementData={movement}
                  onRemove={onRemoveMovementHandler}
                  onGetMovementData={getMovementDataCallBack}
                  editMode={editMode}
                  readOnlyView={readOnlyView}
                />
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
        <div className="flex">
          {editMode && (
            <div>
              <Button variant="outline" aria-label="Add_Movement" onClick={onAddMovementHandler}>
                Movement
                <IconCirclePlus className="ml-1 size-4" />
              </Button>
            </div>
          )}
          {!editMode && (
            <Button
              variant="outline"
              aria-label={readOnlyView ? undefined : "Add_Movement"}
              onClick={() => {
                setShowAddRecord(true);
              }}
            >
              Record results
              <IconCirclePlus className="ml-1 size-4" />
            </Button>
          )}
        </div>
      </Card>
      {workoutUIDRef.current && showAddRecord && (
        <MyModal
          open={showAddRecord}
          onClose={() => setShowAddRecord(false)}
          title="Record results"
          showTitle={false}
        >
          <WorkoutScore
            onSubmitFinished={(saved?: boolean) => saved === true && setShowAddRecord(false)}
            workoutUID={workoutUIDRef.current}
          />
        </MyModal>
      )}
      {!hideActions && readOnlyView && (
        <div data-testid="workout-actions">
          <WorkoutAdditionalAction workoutUID={workoutUIDRef.current} isMyWorkout={false} />
        </div>
      )}
      {!hideActions && !readOnlyView && (
        <motion.div
          animate={actionSectionAnimate}
          initial={{ opacity: 0 }}
          variants={additionalActionVariant}
        >
          <WorkoutAdditionalAction
            workoutUID={workoutUIDRef.current}
            onCopyClicked={onCopyHandle}
            onSavedClicked={onSaveHandle}
            onCancelEditClicked={onCancelEditHandle}
            onEditClicked={onEditHandle}
            onDeleteClicked={onDeleteWorkoutHandle}
            isMyWorkout={isMyWorkout}
            editMode={editMode}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default WorkoutCard;
